import type { ConnectionStatus } from "./types";

/**
 * All module-level state for the shared Reverb WebSocket connection, plus the
 * helpers that operate on it.
 *
 * Bundled into a single object so the singleton can be reset in one place
 * (e.g. in tests via `resetSingleton`).
 */
export const state = {
  _reconnectTimeout: null as ReturnType<typeof setTimeout> | null,
  _pingInterval: null as ReturnType<typeof setInterval> | null,
  _reconnectAttempts: 0,
  _ws: null as WebSocket | null,
  _connecting: false,
  _activeCount: 0,
  _connectFns: new Set<() => void>(),
  _activeUserIds: new Set<string>(),
  _pendingTimers: new Set<ReturnType<typeof setTimeout>>(),
  _status: "disconnected" as ConnectionStatus,
  _statusListeners: new Set<(status: ConnectionStatus) => void>(),
};

export const addTimer = (callback: () => void, delay: number) => {
  const timer = setTimeout(() => {
    state._pendingTimers.delete(timer);
    callback();
  }, delay);

  state._pendingTimers.add(timer);

  return timer;
};

export const clearTimer = (timer: ReturnType<typeof setTimeout> | null) => {
  if (!timer) return;

  clearTimeout(timer);
  state._pendingTimers.delete(timer);
};

export const getStatus = () => state._status;

export const notifyStatus = (status: ConnectionStatus) => {
  state._status = status;

  for (const listener of state._statusListeners) {
    listener(status);
  }
};

/**
 * Reset all singleton state.
 *
 * Useful in tests (e.g. `beforeEach`).
 */
export const resetSingleton = () => {
  if (state._reconnectTimeout) {
    clearTimer(state._reconnectTimeout);
    state._reconnectTimeout = null;
  }

  if (state._pingInterval) {
    clearInterval(state._pingInterval);
    state._pingInterval = null;
  }

  for (const timer of state._pendingTimers) {
    clearTimeout(timer);
  }
  state._pendingTimers.clear();

  if (state._ws) {
    const ws = state._ws;

    ws.onopen = null;
    ws.onmessage = null;
    ws.onerror = null;
    ws.onclose = null;

    if (
      ws.readyState !== WebSocket.CLOSED &&
      ws.readyState !== WebSocket.CLOSING
    ) {
      ws.close(1000, "Singleton reset");
    }
  }

  state._ws = null;
  state._connecting = false;
  state._reconnectAttempts = 0;
  state._connectFns.clear();
  state._activeCount = 0;
  state._activeUserIds.clear();

  notifyStatus("disconnected");
};
